import type { Suggestion } from '@frontend/libs/types/search'
import type React from 'react'

import Search from '@frontend/components/icons/outline/Search'
import useSiteConfiguration from '@frontend/libs/hooks/useSiteConfiguration'
import { useSearchStore } from '@frontend/libs/stores/useSearchStore'
import { ImageSize } from '@frontend/libs/types/media'
import { Button, Input, Spinner } from '@nextui-org/react'
import { ProductImage } from '@scripts/frontend/features/products/components/ProductImage'
import { endpoint } from '@shared/services/endpoint'
import { __ } from '@wordpress/i18n'
import { useCallback, useEffect, useRef, useState } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'

import Drawer from '../../components/drawer/Drawer'
import { SearchResultList } from './components/SearchResultList'

type SearchResponse = {
  engine: string
  suggestions: Suggestion[]
  time: string
  tntTime: string
  total: number
  v: string
}

async function fetchData(keyword: string): Promise<SearchResponse> {
  return await fetch(`${endpoint.frontend.SEARCH.GET}?s=${keyword}`).then(
    (res) => res.json(),
  )
}

function SearchBar() {
  const navigate = useNavigate()
  const location = useLocation()

  const { clearHistory, closeModal, isModalOpen } = useSearchStore(
    (state) => state,
  )
  const { settings } = useSiteConfiguration()

  const [keyword, setKeyword] = useState<string>('')
  const [isLoading, setIsLoading] = useState(false)
  const [suggestions, setSuggestions] = useState<Suggestion[]>([])

  const timeoutRef = useRef<NodeJS.Timeout | null>(null)

  async function handleSearch(keyword: string) {
    inputRef.current?.focus()
    setKeyword(keyword)
    if (keyword.length < 3) return

    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
    }

    timeoutRef.current = setTimeout(async () => {
      setIsLoading(true)
      const { suggestions } = await fetchData(keyword)
      setSuggestions(suggestions)
      setIsLoading(false)
    }, 250)
  }

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
      }
      setKeyword('')
    }
  }, [location])

  const handleOnClearSearchHistory = useCallback(() => {
    clearHistory()
    setKeyword('')
    setSuggestions([])
    closeModal()
  }, [clearHistory, closeModal])

  const inputRef = useRef<HTMLInputElement>(null)

  const handleOnSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    if (keyword.length < 3) return
    navigate(`/shop/?s=${keyword}`)
    closeModal()
  }

  return (
    <Drawer
      isFullHeight
      isOpen={isModalOpen}
      onClose={closeModal}
      position="top"
    >
      <Drawer.Content>
        <div className="pb-10 pt-20 md:p-10 md:py-20">
          <div className="grid grid-cols-1 gap-10 md:gap-16">
            <div className="mx-auto grid w-full max-w-4xl grid-cols-1 gap-10 md:gap-10">
              <form noValidate onSubmit={handleOnSubmit} role="search">
                <Input
                  classNames={{
                    input: '[&>#text-field-container>#search-clear]:hidden',
                  }}
                  enterKeyHint="search"
                  isClearable
                  onValueChange={handleSearch}
                  placeholder={__('What are you looking for?', 'asavagroup')}
                  ref={inputRef}
                  size="lg"
                  startContent={
                    <Search className="block h-10 w-10 !max-w-none" />
                  }
                  type="text"
                  value={keyword}
                  variant="underlined"
                />
              </form>
              <div className="gap-6 md:flex">
                <span className="mb-4 block font-medium uppercase md:mb-0">
                  {__('Trending Searches:', 'asavagroup')}{' '}
                </span>
                {settings?.trendingKeywords?.map((keyword) => (
                  <Button
                    className="h-auto max-h-none min-h-0 p-0"
                    key={keyword}
                    onClick={() => handleSearch(keyword)}
                    onPress={() => handleSearch(keyword)}
                    type="button"
                    variant="light"
                  >
                    {keyword}
                  </Button>
                ))}
              </div>
            </div>
            {isLoading && <Spinner />}
            {!isLoading && (
              <SearchResultList
                keyword={keyword}
                onClearSearchHistory={handleOnClearSearchHistory}
                suggestions={suggestions}
              />
            )}
            {!keyword && (
              <div className="mx-auto max-h-[70dvh] max-w-[1200px] overflow-auto md:max-h-full">
                <span className="mb-4 block text-lg font-medium uppercase md:text-xl">
                  {__('Popular Collection', 'asavagroup')}
                </span>
                <div className="grid grid-cols-2 gap-4 pb-8 md:grid-cols-3">
                  {settings?.popularCollections?.map((collection) => (
                    <div key={collection.title}>
                      <Link
                        className="block"
                        target={collection.link.target}
                        title={collection.link.title}
                        to={collection.link.url}
                      >
                        <ProductImage
                          image={collection.image}
                          size={ImageSize.Medium}
                        />
                        <span className="mt-2 block text-sm font-medium uppercase leading-tight md:text-lg">
                          {collection.title}
                        </span>
                      </Link>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </Drawer.Content>
    </Drawer>
  )
}

export default SearchBar
